// NewFormGenerator.js

import React, { useEffect, useRef, useState } from 'react';
import { Input, Button, VStack, useToast, Spinner, Textarea, Box, Heading, Text, Center, Accordion, AccordionItem, AccordionButton, AccordionIcon, AccordionPanel } from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios'; // You would need to install axios or use any other HTTP client
import { useTranslation } from 'react-i18next'; // Imported hook
//import { getToken } from 'firebase/app-check';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/legacy/build/pdf';
import mixpanel from 'mixpanel-browser';

// Ensure the worker src is set for pdfjs
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.10.377/pdf.worker.min.js`;

const NewAssessmentGenerator = ({iframe, userToken }) => {
  const [fileId, setFileId] = useState(null);
  const [textInstructions, setTextInstructions] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfText, setPdfText] = useState('');
  const [fileName, setFileName] = useState('');
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const useResponses = (process.env.REACT_APP_USE_RESPONSES_API || 'true') === 'true';
  console.log('backend url', backendUrl);
  const hiddenFileInput = React.useRef(null);
  const hasTriggeredFromInitialPromptRef = useRef(false);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const handleInstructionsChange = (event) => {
    setTextInstructions(event.target.value);
  };

  const handleFileChange = (event) => {
    try{
    const file = event.target.files[0];
    if (file.type !== "application/pdf") {
      toast({
        title: t('invalid_file_type'),
        description: t('please_upload_pdf_file'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setIsUploading(true);
    extractTextFromPdf(file);
    setFileName(file.name);
  } catch (error) {
    console.log(error);
  }
  };


  const extractTextFromPdf = (pdfFile) => {
    const fileReader = new FileReader();

    fileReader.onload = async (event) => {
      try {
        const typedArray = new Uint8Array(event.target.result);
        const pdfDocument = await getDocument(typedArray).promise;
        const textContentItems = [];

        for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
          const page = await pdfDocument.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          textContentItems.push(pageText);
        }

        setPdfText(textContentItems.join('\n'));
        console.log('pdfText', textContentItems);
        setIsUploading(false);
      } catch (error) {
        console.error('Error while reading the PDF: ', error);
        toast({
          title: t('error_reading_pdf'),
          description: t('error_extracting_text'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setIsUploading(false);
      }
    };

    fileReader.readAsArrayBuffer(pdfFile);
  };

  const handleFormGeneration = async (forcedInstructionsOrEvent = null) => {
    const forcedInstructions = typeof forcedInstructionsOrEvent === 'string' ? forcedInstructionsOrEvent : null;
    const instructionsToUse = forcedInstructions !== null ? forcedInstructions : (typeof textInstructions === 'string' ? textInstructions : '');
    const safePdfText = typeof pdfText === 'string' ? pdfText : '';


    console.log('pdfText', safePdfText);
    if(!safePdfText.trim() && !instructionsToUse.trim()) {
      toast({
        title: t('no_content_provided'),
        description: t('upload_pdf_or_enter_instructions'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      const textToSend = `${instructionsToUse}\n${safePdfText}`;

      try{
      mixpanel.track('Generate New From', { textToSend });
      } catch (error) {
        console.log(error);
      }

    if (useResponses) {
      const params = {
        userToken: userToken || localStorage.getItem('userToken'),
        message: textToSend
      };
      const aiResponse = await axios.post(`${backendUrl}generateFormWithResponses`, params);
      const runID = aiResponse?.data?.runId;
      if (!aiResponse?.data?.success || !runID) {
        throw new Error(
          aiResponse?.data?.error ||
          t('error_generating_form') ||
          'Invalid response from generation service'
        );
      }
      navigate(`/${iframe ? 'iframe' : 'admin'}/preview/${runID}`);
    } else {
      // Legacy Assistants flow (flagged)
      const params = {
        message: textToSend,
        ...(fileId ? { file_ids: [fileId] } : {})
      };
      const aiResponse = await axios.post(`${backendUrl}runOpenAIAssistant`, params);
      let runID = aiResponse.data.id;
      let threadID = aiResponse.data.thread_id;
      console.log(`/admin/process_generation/${runID}/thread/${threadID}`);
      navigate(`/${iframe ? 'iframe' : 'admin'}/process_generation/${runID}/thread/${threadID}`);
    }

    } catch (error) {
      console.log(error);
      const backendError =
        error?.response?.data?.details?.error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message;
      toast({
        title: t('error_generating_form'),
        description: backendError,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const initialPrompt = (searchParams.get('initial_prompt') || '').trim();
    const hasToken = !!(userToken || localStorage.getItem('userToken'));

    if (!initialPrompt || !hasToken || hasTriggeredFromInitialPromptRef.current || isLoading || isUploading) {
      return;
    }

    hasTriggeredFromInitialPromptRef.current = true;
    setTextInstructions(initialPrompt);

    // Remove the prompt from URL after consuming it to avoid repeated auto-runs on refresh/re-render.
    searchParams.delete('initial_prompt');
    const newSearch = searchParams.toString();
    navigate(
      `${location.pathname}${newSearch ? `?${newSearch}` : ''}`,
      { replace: true }
    );

    handleFormGeneration(initialPrompt);
  }, [location.pathname, location.search, navigate, userToken, isLoading, isUploading]);

  return (
    <Center flexDirection="column" p={{ base: 3, md: 6 }} textAlign="center">
      {!isLoading ? (
        <Box
          w="100%"
          maxW="980px"
          bg="white"
          borderWidth="1px"
          borderColor="blue.100"
          borderRadius="2xl"
          p={{ base: 4, md: 8 }}
          boxShadow="sm"
        >
        <VStack spacing={4} align="stretch">
          <Heading mb={1} textAlign="left">{t('generate_new_assessment_heading')}</Heading>
          <Text mb={2} color="gray.600" textAlign="left">{t('generate_new_assessment_subheading')}</Text>
          {isUploading && <Spinner />}

          {/* Custom file upload button */}
          <Button alignSelf="flex-start" leftIcon={<AttachmentIcon />} colorScheme="blue" variant="outline" onClick={handleClick}>
            {fileName || t('upload_pdf_button')} {/* Display the file name if available */}
          </Button>

          {/* Hidden file input */}
          <Input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            ref={hiddenFileInput}
            display="none"
          />

          {/* Accordion for the editable PDF content */}
          {pdfText && (
            <Accordion allowToggle width="100%" borderWidth="1px" borderColor="gray.100" borderRadius="lg">
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {t('edit_pdf_content')}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Textarea
                    value={pdfText}
                    onChange={(e) => setPdfText(e.target.value)}
                    placeholder={t('pdf_content_placeholder')}
                    size="lg"
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}

          <Textarea
            value={textInstructions}
            onChange={handleInstructionsChange}
            placeholder={t('enter_instructions_placeholder')}
            mb={3}
            minH="130px"
            bg="gray.50"
            borderColor="gray.200"
          />

          <Button
            colorScheme="blue"
            onClick={() => handleFormGeneration()}
            isLoading={isLoading}
            isDisabled={isUploading}
            alignSelf="flex-end"
          >
            {t('generate_assessment_button')}
          </Button>

        </VStack>
        </Box>
      ) : (
        <Center minH="60vh" w="100%">
        <Box
          bg="white"
          borderWidth="1px"
          borderColor="blue.100"
          borderRadius="2xl"
          p={{ base: 6, md: 8 }}
          boxShadow="sm"
          textAlign="center"
        >
          <Spinner size="xl" thickness="4px" speed="0.65s" mb={6} />
          <Heading mb={2}>{t('initiating_assessment_generation')}</Heading>
          <Text color={'gray.500'}>{t('please_wait')}</Text>
        </Box>
        </Center>
      )}
    </Center>
  );
};

export default NewAssessmentGenerator;